<script lang="ts" setup>
  import { StoreLayout } from '@storefront/components';
  import type { OrderResource, ShippingAddress, ListOrderItemResource, Country } from '@storefront/resources';
  import type { Breadcrumb } from '@storefront/types';
  import { type FormContextInterface, useHttpClient } from '@othercommerce/foundation';
  import type { Dictionary } from '@othercommerce/domain';
  import { ref, computed } from 'vue';

  defineOptions({
    layout: [StoreLayout],
    inheritAttrs: false,
  });

  interface ComplaintReturnMethod {
    label: string;
    description: string;
    value: number;
  }

  interface Props {
    items: ListOrderItemResource[];
    complaintReturnMethods: ComplaintReturnMethod[];
    order?: OrderResource
    returnAddress: ShippingAddress;
    countries: Country[],
    regions: Dictionary,
    refund: string,
    breadcrumbs: Breadcrumb[]
  }

  const props = defineProps<Props>();

  const http = useHttpClient();
  const open = ref(false);

  const refundItems = computed(() => {
    return props.items.map(item => ({
      id: item.id,
      photo: item.photo,
      variantTitle: item.variantTitle,
      stockUnit: item.stockUnit,
      amount: item.amount,
      serialNumbers: [''],
    }));
  });

  const data = computed(() => {
    return {
      complaintReturnMethod: props.complaintReturnMethods[0]?.value,
      firstName: props.order?.shippingFirstName,
      lastName: props.order?.shippingLastName,
      email: props.order?.shippingEmail,
      phone: props.order?.shippingPhone,
      countryCode: props.order?.countryCode,
      buildingNr: props.order?.shippingBuildingNr,
      apartmentNr: props.order?.shippingApartmentNr,
      street: props.order?.shippingStreet,
      postcode: props.order?.shippingPostcode,
      city: props.order?.shippingCity,
      region: props.order?.shippingRegion,
      items: refundItems.value,
    };
  });

  function removeSerialNumber(ctx: FormContextInterface, itemIndex: number, serialIndex: number) {
    ctx.data.value.items[itemIndex].serialNumbers.splice(serialIndex, 1);
  }

  function addSerialNumber(ctx: FormContextInterface, itemIndex: number) {
    ctx.data.value.items[itemIndex].serialNumbers.push('');
  }

  function onSubmit(data: any, refund: string) {
    const formData = new FormData();

    formData.append('complaintReturnMethod', data.complaintReturnMethod ?? '');
    formData.append('firstName', data.firstName ?? '');
    formData.append('lastName', data.lastName ?? '');
    formData.append('email', data.email ?? '');
    formData.append('phone', data.phone ?? '');
    formData.append('countryCode', data.countryCode ?? '');
    formData.append('buildingNr', data.buildingNr ?? '');
    formData.append('apartmentNr', data.apartmentNr ?? '');
    formData.append('street', data.street ?? '');
    formData.append('postcode', data.postcode ?? '');
    formData.append('city', data.city ?? '');
    formData.append('region', data.region ?? '');
    formData.append('reason', data.reason ?? '');

    if (Array.isArray(data.items)) {
      data.items.forEach((item: any, i: number) => {
        formData.append(`items[${i}][id]`, item.id);

        if (Array.isArray(item.serialNumbers)) {
          item.serialNumbers.forEach((number: string, numberIndex: number) => {
            formData.append(`items[${i}][serialNumbers][${numberIndex}]`, number);
          });
        }
      });
    }

    const files = data.files instanceof FileList
      ? Array.from(data.files)
      : Array.isArray(data.files)
        ? data.files
        : [];

    for (const file of files) {
      formData.append('files[]', file);
    }

    return http.post(refund, formData);
  }

</script>
<template>
  <main class="refunds">
    <SfBreadcrumbs :breadcrumbs />
    <h1 class="account__heading">
      {{ $t('SHOP.ACCOUNT.HEADER.REFUND_CREATE') }}
    </h1>
    <FormController v-slot="{ processing, data, ctx }" :data="data" @submit="(data) => onSubmit(data, refund)">
      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.REFUND_PRODUCTS') }}</h2>
        </template>
        <template #default>
          <ul class="refunds__items">
            <li v-for="(item, i) in data.items" class="refunds__item item">
              <div class="item__header">
                <div class="item__title">
                  <MediaPicture :photo="item.photo" sizes="160x160 2x, 80x80" />
                  <span>
                    {{ item.variantTitle }} -
                    <SfUnit :quantity="item.amount" :unit="item.stockUnit" />
                  </span>
                </div>
              </div>
              <div class="item__serial-numbers">
                <div v-for="(_, j) in item.serialNumbers" class="item__serial-number">
                  <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.REFUND_PRODUCT_SN', { number: j + 1 })" :name="`items.${i}.serialNumbers[${j}]`" />
                  <SfButton :disabled="data.items[i].serialNumbers.length === 1" :title="$t('SHOP.ACCOUNT.ARIA.REMOVE_PRODUCT')" danger square transparent @click.stop="removeSerialNumber(ctx, i, j)">
                    <template #before>
                      <SfIconTrash aria-hidden="true" />
                    </template>
                  </SfButton>
                </div>
                <SfButton :disabled="data.items[i].serialNumbers.length >= data.items[i].amount" primary small @click="addSerialNumber(ctx, i)">
                  <template #default>
                    {{ $t('SHOP.ACCOUNT.ACTION.REFUND_ADD_ITEM') }}
                  </template>
                </SfButton>
              </div>
            </li>
          </ul>
        </template>
      </SfTile>
      <div class="refunds__details-and-contact-info">
        <SfTile class="refunds__details">
          <template #title>
            <h2>{{ $t('SHOP.ACCOUNT.HEADER.REFUND_DETAILS') }}</h2>
          </template>
          <template #default>
            <SfFormTextarea :label="$t('SHOP.ACCOUNT.LABEL.REFUND_REASON')" name="reason" />
            <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.REFUND_UPLOAD')" multiple name="files" type="file" />

            <div class="refunds__return-methods-wrapper">
              <SfFormSelect :label="$t('SHOP.ACCOUNT.LABEL.REFUND_RETURN_METHOD')" :options="complaintReturnMethods" name="complaintReturnMethod" required />
              <SfButton aria-controls="complaint-return-methods-modal" aria-haspopup="dialog" class="refunds__button-return-methods" @click="open = !open" @keydown.space.enter.prevent="open = !open">
                <template #default>
                  {{ $t('SHOP.ACCOUNT.ACTION.REFUND_MORE_ABOUT_RETURN_METHODS') }}
                </template>
              </SfButton>
            </div>

            <UiModal id="complaint-return-methods-modal" v-model:open="open" aria-labelledby="complaint-return-methods-title" medium>
              <SfModalHeader id="complaint-return-methods-title" v-model:open="open" headingStyle>
                {{ $t('SHOP.ACCOUNT.HEADER.REFUND_CHECK_RETURN_METHODS') }}
              </SfModalHeader>
              <ul class="refunds__return-methods">
                <li v-for="method in complaintReturnMethods">
                  <h3>{{ method.label }}</h3>
                  <p>{{ method.description }}</p>
                </li>
              </ul>
            </UiModal>
          </template>
        </SfTile>

        <SfTile>
          <template #title>
            <h2>{{ $t('SHOP.ACCOUNT.HEADER.REFUND_CONTACT') }}</h2>
          </template>
          <template #default>
            <SfFormInput :label="$t('SHOP.COMMON.LABEL.FIRSTNAME')" name="firstName" required type="text" />
            <SfFormInput :label="$t('SHOP.COMMON.LABEL.LASTNAME')" name="lastName" required type="text" />

            <SfFormInput :label="$t('SHOP.COMMON.LABEL.EMAIL')" name="email" required type="email" />
            <SfFormInput :label="$t('SHOP.COMMON.LABEL.PHONE')" name="phone" required type="tel" />

            <SfAddressForm :countries :regions />
          </template>
        </SfTile>
      </div>
      <SfTile>
        <template #title>
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.REFUND_RETURN_ADDRESS') }}</h2>
        </template>
        <address>
          <p v-if="returnAddress?.countryCode" v-html="returnAddress?.countryCode" />
          <p v-if="returnAddress?.name" v-html="returnAddress?.name" />
          <p v-if="returnAddress?.company" v-html="returnAddress?.company" />
          <p v-if="returnAddress?.firstLine" v-html="returnAddress?.firstLine" />
          <p v-if="returnAddress?.secondLine" v-html="returnAddress?.secondLine" />
          <p v-if="returnAddress?.thirdLine" v-html="returnAddress?.thirdLine" />
          <p v-if="returnAddress?.region" v-html="returnAddress?.region" />
          <p v-if="returnAddress?.email" v-html="returnAddress?.email" />
          <p v-if="returnAddress?.phone" v-html="returnAddress?.phone" />
        </address>
      </SfTile>
      <div class="refunds__button-wrapper">
        <SfButton :loading="processing" primary type="submit">
          <template #default>
            {{ $t('SHOP.ACCOUNT.ACTION.REFUND_CREATE') }}
          </template>
        </SfButton>
      </div>
    </FormController>
  </main>

  <RouterView />
</template>

<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .item {
    list-style: none;
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid var(--secondary-border);
    border-radius: var(--border-radius);

    &__header {
      display: grid;
      grid-template-columns: 1fr;
      align-items: flex-start;
      gap: 1rem;
    }

    &__title {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }

    &__serial-numbers {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__serial-number {
      display: flex;
      gap: 1rem;
    }


    @include media.query(lg) {
      grid-template-columns: 1fr;
      padding: 0.75rem;
    }
  }

  .refunds {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 0 auto 100px;
    width: 98%;
    max-width: var(--sf-account-max-width);

    &__items {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;

      @include media.query(lg) {
        gap: 1rem;
      }
    }

    &__details-and-contact-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }

    &__return-methods-wrapper {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__button-return-methods :deep(.btn__inner) {
      white-space: wrap;
    }

    &__details :deep(.tile__content) {
      justify-content: flex-start;
    }

    &__return-methods {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      h3 {
        font-size: 1.125rem;
        line-height: 150%;
      }
    }

    &__button-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    @include media.query(lg) {
      &__details-and-contact-info {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
